
import { supabase } from '@/integrations/supabase/client';

/**
 * Create or update a user profile in the database
 * Only called after successful payment verification by the edge function
 * This should NOT be called directly from the frontend - maintained for backward compatibility
 */
export const createOrUpdateUserProfile = async (
  email: string, 
  name?: string,
  planId?: string,
  creditAmount?: number
) => {
  if (!email) {
    console.error('Email is required to create a profile');
    return { error: 'Email is required', success: false };
  }

  try {
    // Use the Edge Function for profile creation
    console.log(`DEPRECATED: createOrUpdateUserProfile called directly from frontend for ${email} with ${creditAmount} credits`);
    console.log(`This function should only be called by the edge function after payment verification`);
    console.log(`Forwarding request to create-profile edge function`);
    
    const { data, error } = await supabase.functions.invoke('create-profile', {
      body: {
        userEmail: email,
        userName: name || email.split('@')[0],
        planId,
        creditAmount
      }
    });

    if (error) {
      console.error('Error invoking create-profile function:', error);
      
      // Fallback: Try to insert directly in case edge function fails
      console.log('Attempting direct database insert as fallback');
      
      const fallbackResult = await directProfileCreation(email, name, planId, creditAmount);
      
      return fallbackResult;
    }

    return data;
  } catch (error) {
    console.error('Error creating profile:', error);
    
    // Last resort: Try direct database call
    return await directProfileCreation(email, name, planId, creditAmount);
  }
};

/**
 * Fallback method that uses direct database operations
 * Only used if the Edge Function fails
 */
const directProfileCreation = async (
  email: string, 
  name?: string,
  planId?: string,
  creditAmount?: number
) => {
  try {
    // Calculate compute credits
    let computeCredits = 1000; // Default
    
    if (creditAmount !== undefined && creditAmount !== null) {
      computeCredits = Number(creditAmount);
      console.log(`Using directly provided compute credits: ${computeCredits}`);
    } else if (planId) {
      // Fallback values if plan not found in database
      if (planId === 'professional') {
        computeCredits = 10000000; // 10 million
      } else if (planId === 'starter') {
        computeCredits = 2500000; // 2.5 million
      } else if (planId === 'basic') {
        computeCredits = 1000000; // 1 million
      }
    }
    
    // First check if profile exists
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('*')
      .eq('email', email)
      .maybeSingle();
      
    if (existingProfile) {
      // Profile exists, update it
      const { data, error } = await supabase
        .from('profiles')
        .update({
          name: name || existingProfile.name,
          is_subscribed: Boolean(planId) || (computeCredits > 1000) || existingProfile.is_subscribed,
          compute_points: (existingProfile.compute_points || 0) + computeCredits
        })
        .eq('email', email)
        .select();
        
      if (error) throw error;
      
      return { success: true, profile: data, action: 'updated' };
    } else {
      // Create new profile
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          email,
          name: name || email.split('@')[0],
          is_subscribed: Boolean(planId) || (computeCredits > 1000),
          compute_points: computeCredits
        })
        .select();
        
      if (error) throw error;
      
      return { success: true, profile: data, action: 'created' };
    }
  } catch (error) {
    console.error('Error in direct profile creation:', error);
    return {
      success: false,
      error: 'Failed to create or update profile directly',
      details: error
    };
  }
};

/**
 * Get compute credits for a given plan from database
 */
export const getComputeCreditsForPlan = async (planId: string): Promise<number> => {
  try {
    console.log(`Fetching compute credits for plan: ${planId}`);
    
    // Get plan details from the database
    const { data: planData, error: planError } = await supabase
      .from('plans')
      .select('compute_credits')
      .eq('plan_id', planId)
      .maybeSingle();
    
    if (planError) {
      console.error('Error fetching plan credits from database:', planError);
    } else if (planData && planData.compute_credits) {
      console.log(`Found plan credits in database: ${planData.compute_credits}`);
      return Number(planData.compute_credits);
    }
    
    // Fallback values if database lookup fails
    if (planId === 'professional') {
      return 10000000; // 10 million for Professional plan
    } else if (planId === 'starter') {
      return 2500000; // 2.5 million for Starter plan
    } else if (planId === 'basic') {
      return 1000000; // 1 million for Basic plan (default)
    } else {
      return 1000; // Default minimal credits
    }
  } catch (error) {
    console.error('Error getting compute credits for plan:', error);
    // Fallback values if there's an error
    if (planId === 'professional') {
      return 10000000;
    } else if (planId === 'starter') {
      return 2500000;
    } else {
      return 1000000;
    }
  }
};

// Remove the global set tracking successfully processed emails
// This is now handled entirely in the edge function

/**
 * Update order record with user email - SECURELY via edge function
 */
export const updateOrderWithUserEmail = async (orderId: string, userEmail: string) => {
  try {
    console.log(`Updating order ${orderId} with user email ${userEmail} via edge function`);
    
    // Call the update-order-email edge function - without requiring authentication
    const { data, error } = await supabase.functions.invoke('update-order-email', {
      body: { 
        orderId, 
        userEmail 
      }
    });
      
    if (error) {
      console.error('Error updating order with user email:', error);
      
      // Fallback mechanism - try direct update if the function fails
      try {
        console.log('Attempting direct order update as fallback');
        const { error: directUpdateError } = await supabase
          .from('payment_orders')
          .update({ user_email: userEmail })
          .eq('order_id', orderId);
          
        if (directUpdateError) {
          console.error('Direct order update failed:', directUpdateError);
          return false;
        }
        
        console.log('Direct order update succeeded');
        return true;
      } catch (fallbackError) {
        console.error('Fallback update failed:', fallbackError);
        return false;
      }
    } 
    
    console.log(`Updated order ${orderId} with user email ${userEmail}`);
    return true;
  } catch (error) {
    console.error('Exception updating order:', error);
    return false;
  }
};

/**
 * DEPRECATED: This function should not be used directly from the frontend
 * Verify and ensure a profile exists for the given email using the edge function
 * This is called ONLY after payment verification by the edge function
 */
export const verifyAndEnsureProfile = async (email: string, name?: string, planId?: string, creditAmount?: number) => {
  try {
    console.log(`DEPRECATED: verifyAndEnsureProfile called directly from frontend for ${email}`);
    console.log(`This function should only be called by the edge function after payment verification`);
    
    // Use the edge function to create or update profile
    return await createOrUpdateUserProfile(email, name || email.split('@')[0], planId, creditAmount);
  } catch (error) {
    console.error("ERROR in profile verification process:", error);
    return false;
  }
};
